import React, {
  useState,
  useRef,
  FC,
  useEffect,
  ChangeEvent,
  ReactNode,
  useCallback,
} from 'react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  OnChangeFn,
  Row,
} from '@tanstack/react-table';
import _ from 'underscore';
import LoaderButton from './LoaderButton';
import ToolTip from './ToolTip';
import { Form } from 'react-bootstrap';


interface CustomTableProps {
  data: any[];
  columns: any[];
  paginationTop?: boolean;
  paginationBottom?: boolean;
  addExpandableColumn?: boolean;
  defaultSort?: any[];
  getRowCanExpand?: (row: Row<any>) => boolean;
  subComponentElement?: (row: Row<any>) => ReactNode;
  defaultPageSize?: number;
  className?: string;
  noDataText?: string;
  observeResize?: boolean;
  onCheckmarkChange?: (row: Row<any>) => void;
}

interface TableCompProps {
  updateStartPageSize?: Function;
  expanded: any;
  setExpanded: any; //OnChangeFn<ExpandedState>;
  expandedAll: boolean;
  setExpandedAll: Function;
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
}

const CustomTable: FC<CustomTableProps> = ({
  data,
  columns,
  paginationTop=true,
  paginationBottom=true,
  defaultSort,
  getRowCanExpand,
  addExpandableColumn,
  subComponentElement,
  defaultPageSize = 10,
  className,
  noDataText,
  observeResize=true,
  onCheckmarkChange
}) => {
  const tableElement = useRef<HTMLDivElement>(null);
  const widthRefr = useRef<number | null>(null);
  const [trigerRender, setTrigerRender] = useState<boolean>(true);
  const [startPageSize, setStartPageSize] = useState<number>(defaultPageSize);
  const [expanded, setExpanded] = useState<any>({});
  const [expandedAll, setExpandedAll] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>(defaultSort || []);

  const triggerFunctionOnWidthChange = useCallback(
    _.debounce(() => {
      setTrigerRender(false);
    }, 50),
    []
  );

  useEffect(() => {
    const element = tableElement.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === element) {
          const newWidth = entry.contentRect.width;
          if (newWidth !== widthRefr.current) {
            widthRefr.current = newWidth;
            triggerFunctionOnWidthChange();
          }
        }
      }
    });

    if (observeResize) {
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    if (!trigerRender) {
      setTrigerRender(true);
    }
  }, [trigerRender]);

  return (
    <div className="u-width-100-percent" ref={tableElement}>
      {trigerRender && (
        <TableComp
          data={data}
          columns={columns}
          paginationTop={paginationTop}
          paginationBottom={paginationBottom}
          defaultSort={defaultSort}
          getRowCanExpand={getRowCanExpand}
          addExpandableColumn={addExpandableColumn}
          subComponentElement={subComponentElement}
          defaultPageSize={startPageSize}
          updateStartPageSize={setStartPageSize}
          className={className}
          noDataText={noDataText}
          expanded={expanded}
          setExpanded={(exp: any, clear?: boolean) => {
            if (typeof exp === 'function') {
              setExpanded(exp)
            } else if (!Object.values(exp).length) {
              if(clear) setExpanded(exp);
            } else {
              setExpanded(exp);
            }
          }}
          expandedAll={expandedAll}
          setExpandedAll={setExpandedAll}
          sorting={sorting}
          setSorting={setSorting}
          onCheckmarkChange={onCheckmarkChange}
        />
      )}
    </div>
  );
};

const TableComp: FC<CustomTableProps&TableCompProps> = ({
  data,
  columns,
  paginationTop,
  paginationBottom,
  defaultSort,
  getRowCanExpand,
  addExpandableColumn,
  subComponentElement,
  defaultPageSize = 10,
  className,
  updateStartPageSize,
  noDataText,
  expanded,
  setExpanded,
  expandedAll,
  setExpandedAll,
  sorting,
  setSorting,
  onCheckmarkChange,
}) => {
  const headerRefs = useRef<{ [key: string]: any | null }>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [emptyRows, setEmptyRows] = useState<number>(
    data.length < defaultPageSize ? defaultPageSize - data.length : 0
  );
  const [columnSizing, setColumnSizing] = useState<any>({});
  const [windowWidth, setWindowWidth] = useState<number>(window.outerWidth);

  const expandableColumn = {
    id: 'expander',
    size: 35,
    minSize: 35,
    maxSize: 35,
    enableResizing: false,
    header: ({ table }: { table: any }) => {
      return '';
    },
    cell: ({ row }: { row: any }) => {
      return row.getCanExpand() ? (
        <div
          className="u-cursor-pointer"
          {...{
            onClick: row.getToggleExpandedHandler(),
          }}
        >
          {row.getIsExpanded() ? (
            <i className="u-margin-left u-font-size-large fas fa-chevron-down rt-chevron-item" />
          ) : (
            <i className="u-margin-left u-font-size-large fas fa-chevron-right rt-chevron-item" />
          )}
        </div>
      ) : null;
    },
  };

  const tableColumns = addExpandableColumn
    ? [expandableColumn, ...columns]
    : columns;

  // If needed and if not already existing, add a checkmark column on the end
  if (onCheckmarkChange && !tableColumns.some((col) => col.id === 'checkbox')) {
    tableColumns.push({
      header: '',
      id: 'checkbox',
      size: 35,
      minSize: 35,
      maxSize: 35,
      enableResizing: false,
      cell: ({ row }: { row: any }) => {
        return (
          <div>
            <Form.Check
              type="checkbox"
              id={row.original.rowID}
              checked={row.original.checked}
              onChange={() => onCheckmarkChange(row)}
            />
          </div>
        );
      }
    });
  }

  const handleChangeRowsDisplay = (event: ChangeEvent<HTMLSelectElement>) => {
    setPagination({
      pageIndex: 0,
      pageSize: Number(event.target.value),
    });
    updateStartPageSize?.(Number(event.target.value));
  };

  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowCanExpand,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(data.length / pagination.pageSize),
    onPaginationChange: setPagination,
    state: {
      pagination,
      sorting,
      expanded,
      columnSizing,
    },
    defaultColumn: {
      minSize: 90,
    },
    onColumnSizingChange: setColumnSizing, // Update the state
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  });

  // Expand all rows that have extraInfo
  const handleExpandAll = () => {
    const expandedState: any = {};
    Object.values(table.getRowModel().rowsById).forEach((row: any) => {
      if (row.getCanExpand()) {
        expandedState[row.id] = true;
      }
    });
    setExpanded(expandedState);
  };

  // Collapse all rows
  const handleCollapseAll = () => {
    setExpanded({}, true);
  };

  const handleSortToggle = (columnId: string) => {
    setSorting((prevSorting: any) => {
      const currentSort = prevSorting.find((sort: any) => sort.id === columnId);

      if (!currentSort) {
        // No sort applied, start with ascending
        return [{ id: columnId, desc: false }];
      } else if (!currentSort.desc) {
        // Currently ascending, change to descending
        return [{ id: columnId, desc: true }];
      } else {
        // Currently descending, go back to ascending
        return [{ id: columnId, desc: false }];
      }
    });
  };

  useEffect(() => {
    setEmptyRows(pagination.pageSize - table.getRowModel().rows.length);
  }, [pagination, table]);

  useEffect(() => {
    if (expandedAll) {
      handleExpandAll();
    } else {
      handleCollapseAll();
    }
  }, [expandedAll]);

  useEffect(() => {
    setWindowWidth(window.outerWidth);
    setColumnSizing(headerRefs.current);
  }, []);

  const previousPageButton = (
    <>
      <ToolTip text="First Page">
        <LoaderButton
          className="-btn u-margin-right"
          onClick={() => {
            setEmptyRows(0);
            table.setPageIndex(0);
          }}
          disabled={!table.getCanPreviousPage()}
          icon={<i className="fas fa-angles-left"/>}
        />
      </ToolTip>
      <ToolTip text="Previous Page">
        <LoaderButton
          className="-btn"
          onClick={() => {
            setEmptyRows(0);
            table.previousPage();
          }}
          disabled={!table.getCanPreviousPage()}
          icon={<i className="fas fa-angle-left"/>}
        />
      </ToolTip>
    </>
  );
  const nextPageButton = (
    <>
      <ToolTip text="Next Page">
        <LoaderButton
          className="-btn u-margin-right"
          onClick={() => {
            const currentPage = table.getState().pagination.pageIndex + 1;
            const totalPages = table.getPageCount();
            if (currentPage + 1 === totalPages) {
              const newEmptyRows =
                table.getState().pagination.pageSize * totalPages - data.length;
              setEmptyRows(newEmptyRows);
            }
            table.nextPage();
          }}
          disabled={!table.getCanNextPage()}
          icon={<i className="fas fa-angle-right"/>}
        />
      </ToolTip>
      <ToolTip text="Last Page">
        <LoaderButton
          className="-btn"
          onClick={() => {
            const totalPages = table.getPageCount();
            const newEmptyRows =
              table.getState().pagination.pageSize * totalPages - data.length;
            setEmptyRows(newEmptyRows);
            table.setPageIndex(totalPages - 1);
          }}
          disabled={!table.getCanNextPage()}
          icon={<i className="fas fa-angles-right"/>}
        />
      </ToolTip>
    </>
  );

  const paginationElement = (
    <div className="-pagination">
      <div className="-previous">
        {previousPageButton}
      </div>
      {windowWidth <= 460 &&
        <div className="-next">
          {nextPageButton}
        </div>
      }
      <div className="-center l-row-gap-1rem">
        <span className="-pageInfo">
          Page{' '}
          <div className="-pageJump">
            <input
              type="number"
              value={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const totalPages = table.getPageCount();
                const numberValue = Number(e.target.value);
                const destinationPage =
                  numberValue < 1
                    ? 1
                    : numberValue >= totalPages
                    ? totalPages
                    : numberValue;
                if (destinationPage === totalPages) {
                  const newEmptyRows =
                    table.getState().pagination.pageSize * totalPages -
                    data.length;
                  setEmptyRows(newEmptyRows);
                } else {
                  setEmptyRows(0);
                }
                table.setPageIndex(destinationPage - 1);
              }}
            />
          </div>{' '}
          of{' '}
          <span className="-totalPages">{table.getPageCount() || 1}</span>
        </span>
        <span className="select-wrap -pageSizeOptions">
          <select
            value={table.getState().pagination.pageSize}
            onChange={handleChangeRowsDisplay}
          >
            {[5, 10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {`${size} rows`}
              </option>
            ))}
          </select>
        </span>
      </div>
      {windowWidth > 460 &&
        <div className="-next">
          {nextPageButton}
        </div>
      }
    </div>
  );

  return (
    <div className={`ReactTable -highlight ${className ?? ''}`}>
      {paginationTop && (
        <div className="pagination-top">{paginationElement}</div>
      )}
      <div className="rt-table">
        {/* Header */}
        <div
          className={`rt-thead -header ${paginationTop && 'no-border-radius-top'}`}
          style={{
            minWidth: table.getTotalSize(),
          }}
        >
          {table.getHeaderGroups().map((headerGroup: any) => (
            <div key={headerGroup.id} className="rt-tr">
              {headerGroup.headers.map((header: any) => {
                return (
                  <div
                    key={header.id}
                    ref={(el) => {
                      // Store the ref for each header
                      if (el && !headerRefs.current[header.id]) {
                        headerRefs.current[header.id] = el.offsetWidth;
                        if (
                          tableColumns.length ===
                          Object.keys(headerRefs.current).length
                        ) {
                          setColumnSizing(headerRefs.current);
                        }
                      }
                    }}
                    className={`rt-header-cell ${header.column.columnDef.headerCssClass ?? ''}`}
                    style={{
                      width: header.getSize(),
                      minWidth: header.getSize(),
                      flex:
                        header.column.columnDef.enableGrow &&
                        !headerRefs.current[header.id]
                          ? '100 0 auto'
                          : undefined,
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <span
                        className={`display-flex align-items-center ${header.column.accessorFn ? 'u-cursor-pointer u-padding-right-large' : ''}`}
                        onClick={
                          !header.column.getCanSort()
                            ? undefined
                            : () => handleSortToggle(header.column.id)
                        }
                      >
                        {{
                          asc: (
                            <span className="display-flex align-items-center u-width-20px">
                              <i className="u-margin-right fas fa-arrow-down" />
                            </span>
                          ),
                          desc: (
                            <span className="display-flex align-items-center u-width-20px">
                              <i className="u-margin-right fas fa-arrow-up" />
                            </span>
                          ),
                        }[header.column.getIsSorted() as string] ?? null}
                        <span className="overflow-text-ellipsis">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                      </span>
                    )}
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`rt-resizer`}
                      >
                        <div className="rt-resizer-column" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Body */}
        <div
          className="rt-tbody"
          style={{
            minWidth: table.getTotalSize(),
          }}
        >
          {table.getRowModel().rows.map((row: any, idx: number) => (
            <div className="rt-tr-group" key={row.id}>
              <div className={`rt-tr ${idx % 2 === 0 ? '-even' : '-odd'}`}>
                {row.getVisibleCells().map((cell: any) => {
                  return (
                    <div
                      key={cell.id}
                      className={`rt-cell ${
                        cell.column.columnDef.className ?? ''
                      }`}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                        flex:
                          cell.column.columnDef.enableGrow &&
                          !headerRefs.current[cell.column.id]
                            ? '100 0 auto'
                            : undefined,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  );
                })}
              </div>
              {row.getIsExpanded() && (
                <div className="u-margin-bottom">
                  {subComponentElement?.(row)}
                </div>
              )}
            </div>
          ))}
          {!!emptyRows &&
            Array.from({ length: emptyRows }).map((_, idx) => (
              <div className="rt-tr-group" key={idx}>
                <div className="rt-tr -padRow">
                  <div className="rt-cell"></div>
                </div>
              </div>
            ))}
        </div>
      </div>
      {paginationBottom && (
        <div className="pagination-bottom">{paginationElement}</div>
      )}
      { data.length === 0 &&
        <div className="rt-noData">
          <span>{noDataText || "No Data Yet"}</span>
        </div>
      }
    </div>
  );
};

export default CustomTable;
